import { useEffect, useMemo, useState } from "react";
import { Brain, RefreshCw, AlertCircle, Building2, ChevronDown } from "lucide-react";
import { useOrgMemory } from "../hooks/useOrgMemory";
import { GlobalMemoryHeader } from "../components/memory/GlobalMemoryHeader";
import { MemorySectionPanel } from "../components/memory/MemorySectionPanel";
import { orgMemoryManagerService } from "../services/orgMemoryManagerService";
import { toast } from "@foundation/hooks/use-toast";
import type { MemorySection } from "../types/orgMemory";
import RevisrLayout from "../components/RevisrLayout";
import { OrgProfileForm } from "./OrganisationProfilePage";

export const OrgMemoryManagerPage = () => {
  const [profileOpen, setProfileOpen] = useState(true);
  const [categories, setCategories] = useState<string[]>([]);
  const {
    structure, isLoading, isSaving, error,
    saveEntry, deleteEntry, updateSection, deleteSection, updateGlobal, updateFeedback,
    save, reload,
  } = useOrgMemory();

  useEffect(() => {
    orgMemoryManagerService
      .getCategories()
      .then(setCategories)
      .catch((err) => console.error("[OrgMemory] category load error:", err));
  }, []);

  const groupedByCategory = useMemo((): Map<string, MemorySection[]> => {
    if (!structure) return new Map();
    const map = new Map<string, MemorySection[]>();
    for (const section of structure.sections) {
      const key = section.category || "Uncategorised";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(section);
    }
    return map;
  }, [structure]);

  const totalEntries = useMemo(
    () => structure?.sections.reduce((sum, s) => sum + s.entries.length, 0) ?? 0,
    [structure]
  );

  const handleSave = async () => {
    try {
      await save();
      toast({ title: "Memory saved", description: "Changes written to org memory." });
    } catch {
      toast({ title: "Save failed", description: "Could not write to storage.", variant: "destructive" });
    }
  };

  const handleSaveEntry = async (sectionId: string, entryId: string, updates: { answer: string }) => {
    try {
      await saveEntry(sectionId, entryId, updates);
      toast({ title: "Answer saved", description: "Written to org memory." });
    } catch {
      toast({ title: "Save failed", description: "Could not write to storage.", variant: "destructive" });
    }
  };

  return (
    <RevisrLayout>
    <div className="mx-auto max-w-4xl px-4 py-8">
      {/* Organisation Profile — collapsible */}
      <div className="mb-6 rounded-xl border border-border bg-card shadow-sm">
        <button
          type="button"
          onClick={() => setProfileOpen((o) => !o)}
          className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
          aria-expanded={profileOpen}
        >
          <span className="flex items-center gap-3">
            <Building2 className="size-6 text-[#33b864] shrink-0" />
            <span>
              <span className="block text-lg font-bold text-foreground">Organisation Profile</span>
              <span className="block text-sm text-muted-foreground">
                Tailors generated policies to your organisation
              </span>
            </span>
          </span>
          <ChevronDown
            className={`size-5 text-muted-foreground transition-transform ${profileOpen ? "rotate-180" : ""}`}
          />
        </button>
        {profileOpen && (
          <div className="border-t border-border px-5 py-5">
            <OrgProfileForm showHeader={false} />
          </div>
        )}
      </div>

      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Brain className="size-7 text-emerald-600 shrink-0" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Org Memory</h1>
            <p className="text-sm text-gray-500">
              {totalEntries} Q&A {totalEntries === 1 ? "entry" : "entries"} across{" "}
              {structure?.sections.length ?? 0} sections
            </p>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={reload}
            disabled={isLoading}
            className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`size-4 ${isLoading ? "animate-spin" : ""}`} />
            Reload
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="size-4 shrink-0" /> {error}
        </div>
      )}

      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-20 rounded-xl bg-gray-100 animate-pulse" />)}
        </div>
      )}

      {!isLoading && structure && (
        <div className="space-y-6">
          <GlobalMemoryHeader
            categories={structure.categories}
            globalTags={structure.globalTags}
            feedback={structure.feedback ?? []}
            isSaving={isSaving}
            onUpdateGlobal={updateGlobal}
            onUpdateFeedback={updateFeedback}
            onSave={handleSave}
          />

          {structure.sections.length === 0 && (
            <div className="rounded-xl border-2 border-dashed border-gray-200 py-16 text-center">
              <Brain className="mx-auto mb-3 size-10 text-gray-300" />
              <p className="text-gray-500">No memory entries yet.</p>
              <p className="text-sm text-gray-400">Complete a Boost Policy Q&A session to populate memory.</p>
            </div>
          )}

          {Array.from(groupedByCategory.entries()).map(([categoryName, sections]) => (
            <div key={categoryName}>
              <h2 className="mb-3 text-xs font-bold uppercase tracking-widest text-gray-400">
                {categoryName}
              </h2>
              <div className="space-y-3">
                {sections.map((section) => (
                  <MemorySectionPanel
                    key={section.id}
                    section={section}
                    categories={categories}
                    isSaving={isSaving}
                    onUpdateSection={(updates) => updateSection(section.id, updates)}
                    onSaveEntry={(entryId, updates) => handleSaveEntry(section.id, entryId, updates)}
                    onDeleteEntry={(entryId) => deleteEntry(section.id, entryId)}
                    onDeleteSection={() => deleteSection(section.id)}
                    onSave={handleSave}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    </RevisrLayout>
  );
};
